import { useState, useCallback, useMemo } from 'react'

import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Form, message, notification, Space, Typography } from 'antd'
import { RcFile } from 'antd/es/upload'
import { uniqueId, isEqual } from 'lodash'
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs'
import { useNavigate, useBlocker } from 'react-router-dom'

import useUploadFile from '@src/hooks/queries/file/useUploadFile'
import { resizeImage, compressImage } from '@src/utils/image'
import vendorAtom from '@src/atom/vendor'
import { isAllowModalOpenedAtom, isCompanyPreviewModalOpenedAtom } from '@src/atom/ui'
import { CreateHotdealChallengeDto, DraftHotdealChallengeDto } from '@src/type/challenge'
import { useCreateHotdealChallengeMutate } from '@src/hooks/queries/challenge/useCreateHotdealChallengeMutate'
import { useDraftHotdealChallengeMutate } from '@src/hooks/queries/challenge/useDraftHotdealChallengeMutate'
import { useGetCardQuery } from '@src/hooks/queries/payment/useGetCardQuery'
import { useGetCompanyChallengeCategoriesQuery } from '@src/hooks/queries/challenge/useGetCompanyChallengeCategoriesQuery'
import { CardDto, GetCardQueryKey } from '@src/type/payment'
import { CreateHotdealChallengeForm, DescriptionImage } from '../type'
import { Topic, TopicOption } from '../../type'
import { prepareTopic } from '../../util'

const { Text } = Typography

export function useHotdealChallengeRegistrationPage() {
	const navigate = useNavigate()

	const vendor = useRecoilValue(vendorAtom)

	const { data: companyChallengeCategories } = useGetCompanyChallengeCategoriesQuery()

	const { topics, topicOptions } = useMemo<{
		topics: Topic[]
		topicOptions: TopicOption[]
	}>(() => {
		if (!companyChallengeCategories) {
			return { topics: [], topicOptions: [] }
		}

		return prepareTopic(companyChallengeCategories)
	}, [companyChallengeCategories])

	const [isEtcTopicModalOpened, setIsEtcTopicModalOpened] = useState(false)

	const onEtcTopicModalCanceled = useCallback(() => {
		setIsEtcTopicModalOpened(false)
	}, [])

	const [form] = Form.useForm<CreateHotdealChallengeForm>()

	const initialFormValue = useMemo(
		() => ({
			topic: [],
			thumbnail: null,
			descriptionImages: [],
			title: '',
			beforePrice: null,
			afterPrice: null,
			link: '',
			startDate: null,
		}),
		[],
	)

	const [formValue, setFormValue] = useState<CreateHotdealChallengeForm>(initialFormValue)

	const onFormChanged = useCallback(
		(changedValues: Record<string, unknown>, values: CreateHotdealChallengeForm) => {
			setFormValue(values)

			if (
				'topic' in changedValues &&
				Array.isArray(changedValues.topic) &&
				changedValues.topic.length > 0 &&
				changedValues.topic[0] === 0
			) {
				setIsEtcTopicModalOpened(true)
			}
		},
		[],
	)

	const topic = Form.useWatch('topic', form)

	const selectedTopicName = useMemo<string | undefined>(() => {
		if (!topic || topic.length < 2 || topic.at(0) === 0) {
			return undefined
		}

		const topicValue = topics.find((t) => t.id === topic[1])

		return topicValue?.name
	}, [topic, topics])

	const [hasThumbnailError, setHasThumbnailError] = useState(false)
	const [hasStartDateError, setHasStartDateError] = useState(false)

	const thumbnail = Form.useWatch('thumbnail', form)

	const onThumbnailUploaded = useCallback(
		async (file: RcFile) => {
			let thumbnailBlob = await resizeImage(file, 560, Infinity)

			if (thumbnailBlob.size > 3000000) {
				thumbnailBlob = await compressImage(thumbnailBlob, {
					maxSizeMB: 3,
					maxWidthOrHeight: Infinity,
					alwaysKeepResolution: true,
				})
			}

			form.setFieldValue('thumbnail', thumbnailBlob)
			setFormValue(form.getFieldsValue())
			setHasThumbnailError(false)
		},
		[form],
	)

	const onThumbnailRemoved = useCallback(() => {
		form.setFieldValue('thumbnail', null)
		setFormValue(form.getFieldsValue())
	}, [form])

	const descriptionImages = Form.useWatch('descriptionImages', form)

	const onDescriptionImageUploaded = useCallback(
		async (file: RcFile) => {
			const prevDescriptionIds = form.getFieldValue('descriptionImages') as DescriptionImage[]

			if (prevDescriptionIds.length >= 10) {
				return
			}

			const id = `description_image_${uniqueId()}`

			form.setFieldValue('descriptionImages', [...prevDescriptionIds, { id }])

			let descriptionImageBlob = await resizeImage(file, 750, Infinity)

			if (descriptionImageBlob.size > 3000000) {
				descriptionImageBlob = await compressImage(descriptionImageBlob, {
					maxSizeMB: 3,
					maxWidthOrHeight: Infinity,
					alwaysKeepResolution: true,
				})
			}

			const prevDescriptionImages = form.getFieldValue('descriptionImages') as DescriptionImage[]

			const newDescriptionImages = prevDescriptionImages.map((prevDescriptionImage) => {
				if (prevDescriptionImage.id !== id) {
					return prevDescriptionImage
				}

				return {
					...prevDescriptionImage,
					value: descriptionImageBlob,
				}
			})

			form.setFieldValue('descriptionImages', newDescriptionImages)
			setFormValue(form.getFieldsValue())
		},
		[form],
	)

	const onDescriptionImageRemoved = useCallback(
		(targetId: string) => {
			const prevDescriptionImages = form.getFieldValue('descriptionImages') as DescriptionImage[]

			const newDescriptionImages = prevDescriptionImages.filter(
				(prevDescriptionImage) => prevDescriptionImage.id !== targetId,
			)

			form.setFieldValue('descriptionImages', newDescriptionImages)
			setFormValue(form.getFieldsValue())
		},
		[form],
	)

	const startDate = Form.useWatch('startDate', form)

	const onStartDateChanged = useCallback(
		(date: dayjs.Dayjs) => {
			form.setFieldValue('startDate', date)
			setFormValue(form.getFieldsValue())
			setHasStartDateError(false)
		},
		[form],
	)

	const canCreateHotdealChallenge = useMemo<boolean>(() => {
		return (
			formValue.topic.length > 0 ||
			formValue.thumbnail !== null ||
			formValue.title !== '' ||
			formValue.beforePrice !== null ||
			formValue.afterPrice !== null ||
			formValue.link !== '' ||
			formValue.startDate !== null
		)
	}, [formValue])

	const canDraftHotdealChallenge = useMemo<boolean>(() => {
		return (
			formValue.topic.length > 0 ||
			formValue.thumbnail !== null ||
			formValue.title !== '' ||
			formValue.beforePrice !== null ||
			formValue.afterPrice !== null ||
			formValue.link !== '' ||
			formValue.startDate !== null
		)
	}, [formValue])

	const { mutateAsync: createHotdealChallengeMutate } = useCreateHotdealChallengeMutate()

	const { mutateAsync: draftHotdealChallengeMutate } = useDraftHotdealChallengeMutate()

	const { mutateAsync: uploadFileMutate } = useUploadFile()

	const vendorNo = useMemo(() => {
		const parsedVendorNo = Number.parseInt(vendor.no, 10)
		const no = Number.isNaN(parsedVendorNo) ? undefined : parsedVendorNo

		return no
	}, [vendor])

	const queryKey = useMemo<GetCardQueryKey>(() => ['getCard', vendorNo], [vendorNo])

	const { data: cardResBody } = useGetCardQuery({ queryKey })

	const card = useMemo<CardDto | null | undefined>(() => {
		if (!cardResBody) {
			return undefined
		}

		if (cardResBody.data.card_info_list.length <= 0) {
			return null
		}

		return cardResBody.data.card_info_list[0]
	}, [cardResBody])

	const [isCardRequireModalOpened, setIsCardRequireModalOpened] = useState(false)

	const onCardRequireModalCanceled = useCallback(() => {
		setIsCardRequireModalOpened(false)
	}, [])

	const [isCreating, setIsCreating] = useState(false)
	const [isDrafting, setIsDrafting] = useState(false)

	const setIsCompanyPreviewModalOpened = useSetRecoilState(isCompanyPreviewModalOpenedAtom)
	const setIsAllowModalOpened = useSetRecoilState(isAllowModalOpenedAtom)

	const onRegistrationClicked = useCallback(async () => {
		if (isCreating || isDrafting) {
			return
		}

		if (card === undefined) {
			await message.error('권한 확인을 위한 데이터가 없습니다. 새로고침 후 다시 시도해주세요.')
			return
		}

		if (card === null) {
			setIsCardRequireModalOpened(true)
			return
		}

		try {
			await form.validateFields()
		} catch (errorInfo) {
			const { errorFields } = errorInfo as {
				errorFields: Array<{ errors: string[]; name: string[] }>
			}

			errorFields.forEach(({ name: [fieldName] }) => {
				if (fieldName === 'thumbnail') {
					setHasThumbnailError(true)
				}

				if (fieldName === 'startDate') {
					setHasStartDateError(true)
				}
			})

			const firstFieldName = errorFields[0].name[0]
			const firstArea = document.getElementById(`creation-form-${firstFieldName}`)

			if (firstArea) {
				firstArea.scrollIntoView({ behavior: 'smooth' })
			}

			return
		}

		try {
			setIsCreating(true)

			const thumbnailFormData = new FormData()
			thumbnailFormData.append('file', formValue.thumbnail!)
			thumbnailFormData.append('path', 'hotdeal/hotdeal_product_img/')
			const thumbnailUploadPromise = uploadFileMutate(thumbnailFormData)

			const challengeCoverFormData = new FormData()
			challengeCoverFormData.append('file', formValue.thumbnail!)
			challengeCoverFormData.append('path', 'campaign_cover_img/')
			const challengeCoverUploadPromise = uploadFileMutate(challengeCoverFormData)

			const challengeBannerFormData = new FormData()
			challengeBannerFormData.append('file', formValue.thumbnail!)
			challengeBannerFormData.append('path', 'campaign_banner_img/')
			const challengeBannerUploadPromise = uploadFileMutate(challengeBannerFormData)

			const descriptionImageUploadPromises = formValue.descriptionImages.map((descriptionImage) => {
				const descriptionImageFormData = new FormData()
				descriptionImageFormData.append('file', descriptionImage.value!)
				descriptionImageFormData.append('path', 'campaign_description_img/')
				return uploadFileMutate(descriptionImageFormData)
			})

			const [
				thumbnailUploadResponse,
				challengeCoverUploadResponse,
				challengeBannerUploadResponse,
				...descriptionImageUploadResponses
			] = await Promise.all([
				thumbnailUploadPromise,
				challengeCoverUploadPromise,
				challengeBannerUploadPromise,
				...descriptionImageUploadPromises,
			])

			if (
				thumbnailUploadResponse.success !== 1 ||
				challengeCoverUploadResponse.success !== 1 ||
				challengeBannerUploadResponse.success !== 1 ||
				descriptionImageUploadResponses.some(
					(descriptionImageUploadResponse) => descriptionImageUploadResponse.success !== 1,
				)
			) {
				throw new Error('upload fail')
			}

			const thumbnailFileName = thumbnailUploadResponse.data.upload_results[0].file_name
			const challengeCoverFileName = challengeCoverUploadResponse.data.upload_results[0].file_name
			const challengeBannerFileName = challengeBannerUploadResponse.data.upload_results[0].file_name
			const descriptionImageFileNames = descriptionImageUploadResponses.map(
				(descriptionImageUploadResponse) =>
					descriptionImageUploadResponse.data.upload_results[0].file_name,
			)

			const topicValue = topics.find((t) => t.id === formValue.topic[1])

			if (!topicValue) {
				throw new Error(`알 수 없는 챌린지 주제가 선택되었습니다: id: ${formValue.topic[1]}`)
			}

			const createHotdealChallengeDto: CreateHotdealChallengeDto = {
				company_no: vendor.no,
				is_temporary: false,
				sticker_no: topicValue.stickers.at(0) || '64',
				title: `[${vendor.brandName}]${topicValue.name} 핫딜 챌린지`,
				category_no: topicValue.challengeCategories,
				sample_proof: topicValue.sampleProofs,
				task_name: topicValue.name,
				product_img: thumbnailFileName,
				cover_img: challengeCoverFileName,
				banner_img: challengeBannerFileName,
				detail_descriptions: descriptionImageFileNames,
				product_name: formValue.title,
				price: formValue.beforePrice!,
				discount_price: formValue.afterPrice!,
				product_link: formValue.link,
				exposure_date: formValue.startDate!.toDate(),
				start_date: formValue.startDate!.add(2, 'day').toDate(),
				end_date: formValue.startDate!.add(8, 'day').toDate(),
				days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
			}

			await createHotdealChallengeMutate(createHotdealChallengeDto)

			form.resetFields()
			setFormValue(form.getFieldsValue)

			await new Promise((resolve) => {
				setTimeout(resolve, 100)
			})

			navigate('/challenge')

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			message.success('상품 등록 성공!')

			// setIsCompanyPreviewModalOpened(true)
			notification.config({
				duration: 0,
				placement: 'bottomLeft',
				bottom: 154,
			})
			notification.open({
				message: (
					<Space direction="horizontal" size="large">
						<Text>💁‍♀️ 생성한 챌린지를 모티너스 앱에 노출하고 싶다면?</Text>
						<Text
							underline
							style={{ cursor: 'pointer' }}
							onClick={() => {
								setIsAllowModalOpened(true)
								notification.destroy()
							}}
						>
							1:1문의
						</Text>
					</Space>
				),
				className: 'motiners-notification',
			})
		} catch (e) {
			console.log(e)
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			message.error('상품 등록 실패')
		} finally {
			setIsCreating(false)
		}
	}, [
		vendor,
		form,
		formValue,
		createHotdealChallengeMutate,
		uploadFileMutate,
		navigate,
		card,
		isCreating,
		isDrafting,
		// setIsCompanyPreviewModalOpened,
		setIsAllowModalOpened,
		topics,
	])

	const onDraftClicked = useCallback(async () => {
		if (isCreating || isDrafting) {
			return
		}

		try {
			setIsDrafting(true)

			const draftHotdealChallengeDto: DraftHotdealChallengeDto = {
				company_no: vendor.no,
				is_temporary: true,
			}

			if (formValue.topic.length > 0 && formValue.topic[0] !== 0) {
				const topicValue = topics.find((t) => t.id === formValue.topic[1])

				if (!topicValue) {
					throw new Error(`알 수 없는 챌린지 주제가 선택되었습니다: id: ${formValue.topic[1]}`)
				}

				draftHotdealChallengeDto.title = `[${vendor.brandName}]${topicValue.name} 핫딜 챌린지`
				draftHotdealChallengeDto.category_no = topicValue.challengeCategories
				draftHotdealChallengeDto.sample_proof = topicValue.sampleProofs
				draftHotdealChallengeDto.task_name = topicValue.name
				draftHotdealChallengeDto.sticker_no = topicValue.stickers.at(0) || '64'
			}

			if (formValue.thumbnail) {
				const thumbnailFormData = new FormData()
				thumbnailFormData.append('file', formValue.thumbnail)
				thumbnailFormData.append('path', 'hotdeal/hotdeal_product_img/')
				const thumbnailUploadPromise = uploadFileMutate(thumbnailFormData)

				const challengeCoverFormData = new FormData()
				challengeCoverFormData.append('file', formValue.thumbnail)
				challengeCoverFormData.append('path', 'campaign_cover_img/')
				const challengeCoverUploadPromise = uploadFileMutate(challengeCoverFormData)

				const challengeBannerFormData = new FormData()
				challengeBannerFormData.append('file', formValue.thumbnail)
				challengeBannerFormData.append('path', 'campaign_banner_img/')
				const challengeBannerUploadPromise = uploadFileMutate(challengeBannerFormData)

				const [
					thumbnailUploadResponse,
					challengeCoverUploadResponse,
					challengeBannerUploadResponse,
				] = await Promise.all([
					thumbnailUploadPromise,
					challengeCoverUploadPromise,
					challengeBannerUploadPromise,
				])

				if (
					thumbnailUploadResponse.success !== 1 ||
					challengeCoverUploadResponse.success !== 1 ||
					challengeBannerUploadResponse.success !== 1
				) {
					throw new Error('upload fail')
				}

				const thumbnailFileName = thumbnailUploadResponse.data.upload_results[0].file_name
				const challengeCoverFileName = challengeCoverUploadResponse.data.upload_results[0].file_name
				const challengeBannerFileName =
					challengeBannerUploadResponse.data.upload_results[0].file_name

				draftHotdealChallengeDto.product_img = thumbnailFileName
				draftHotdealChallengeDto.cover_img = challengeCoverFileName
				draftHotdealChallengeDto.banner_img = challengeBannerFileName
			}

			if (formValue.descriptionImages.length > 0) {
				const descriptionImageUploadPromises = formValue.descriptionImages.map(
					(descriptionImage) => {
						const descriptionImageFormData = new FormData()
						descriptionImageFormData.append('file', descriptionImage.value!)
						descriptionImageFormData.append('path', 'campaign_description_img/')
						return uploadFileMutate(descriptionImageFormData)
					},
				)

				const descriptionImageUploadResponses = await Promise.all(descriptionImageUploadPromises)

				if (
					descriptionImageUploadResponses.some(
						(descriptionImageUploadResponse) => descriptionImageUploadResponse.success !== 1,
					)
				) {
					throw new Error('upload fail')
				}

				const descriptionImageFileNames = descriptionImageUploadResponses.map(
					(descriptionImageUploadResponse) =>
						descriptionImageUploadResponse.data.upload_results[0].file_name,
				)

				draftHotdealChallengeDto.detail_descriptions = descriptionImageFileNames
			}

			if (formValue.title !== '') {
				draftHotdealChallengeDto.product_name = formValue.title
			}

			if (formValue.beforePrice !== null) {
				draftHotdealChallengeDto.price = formValue.beforePrice
			}

			if (formValue.afterPrice !== null) {
				draftHotdealChallengeDto.discount_price = formValue.afterPrice
			}

			if (formValue.link !== '') {
				draftHotdealChallengeDto.product_link = formValue.link
			}

			if (formValue.startDate !== null) {
				draftHotdealChallengeDto.exposure_date = formValue.startDate.toDate()
				draftHotdealChallengeDto.start_date = formValue.startDate.add(2, 'day').toDate()
				draftHotdealChallengeDto.end_date = formValue.startDate.add(8, 'day').toDate()
				draftHotdealChallengeDto.days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
			}

			await draftHotdealChallengeMutate(draftHotdealChallengeDto)

			form.resetFields()
			setFormValue(form.getFieldsValue())

			await new Promise((resolve) => {
				setTimeout(resolve, 100)
			})

			navigate('/challenge')

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			message.success('임시 저장되었어요')
		} catch (e) {
			console.log(e)
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			message.error('임시 저장 실패')
		} finally {
			setIsDrafting(false)
		}
	}, [
		vendor,
		formValue,
		form,
		draftHotdealChallengeMutate,
		uploadFileMutate,
		navigate,
		isCreating,
		isDrafting,
		topics,
	])

	const onGuidedDraftClicked = useCallback(async () => {
		await onDraftClicked()

		notification.config({
			duration: 0,
			placement: 'bottomLeft',
			bottom: 154,
		})
		notification.open({
			message: (
				<Space direction="horizontal" size="large">
					<Text>💁‍♀️ 이제 카드 등록을 해볼까요?</Text>
					<Text
						underline
						style={{ cursor: 'pointer' }}
						onClick={() => {
							navigate('/payment/subscribe/plan')
							notification.destroy()
						}}
					>
						바로 가기
					</Text>
				</Space>
			),
			className: 'motiners-notification',
		})
	}, [onDraftClicked, navigate])

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return !isEqual(formValue, initialFormValue) && currentLocation !== nextLocation
	})

	return {
		topicOptions,
		isEtcTopicModalOpened,
		onEtcTopicModalCanceled,
		selectedTopicName,
		form,
		initialFormValue,
		onFormChanged,
		hasThumbnailError,
		hasStartDateError,
		thumbnail,
		onThumbnailUploaded,
		onThumbnailRemoved,
		descriptionImages,
		onDescriptionImageUploaded,
		onDescriptionImageRemoved,
		startDate,
		onStartDateChanged,
		onRegistrationClicked,
		onDraftClicked,
		onGuidedDraftClicked,
		isCreating,
		isDrafting,
		canCreateHotdealChallenge,
		canDraftHotdealChallenge,
		blocker,
		isCardRequireModalOpened,
		onCardRequireModalCanceled,
	}
}
